import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Search, BadgeCheck, ChevronDown, ChevronRight, Globe } from "lucide-react";
import { ResourceCard } from "@/components/catalog-card";
import { RegistryImportDialog } from "@/components/registry-import";
import { CloudIcon } from "@/components/brand-icons";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ConfirmDeleteDialog } from "@/components/confirm-delete-dialog";
import * as yaml from "js-yaml";

const CATEGORIES = ["Compute", "Storage", "Database", "Networking", "Security", "Other"];

const PLACEHOLDER_HINT = "Placeholders: ${name} ${location} ${tags} ${resource_group} ${label} ${param.<default-key>}. Leave empty to let Toastern generate a compliant skeleton from the resource type.";

function CodeTemplates({ value, onChange, testidPrefix }) {
  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid={`${testidPrefix}-templates`}>
      <div>
        <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// code templates (optional)</p>
        <p className="mt-1 text-xs text-slate-500">{PLACEHOLDER_HINT}</p>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500">Terraform template</Label>
        <Textarea rows={5} spellCheck={false} data-testid={`${testidPrefix}-tf-code`} className="mt-2 border-slate-200 font-mono-tight text-xs" value={value.terraform_code} onChange={(e) => onChange({ ...value, terraform_code: e.target.value })} placeholder={'resource "azurerm_cosmosdb_account" "${label}" {\n  name                = "${name}"\n  location            = ${location}\n  resource_group_name = ${resource_group}\n  offer_type          = "${param.offer_type}"\n  tags                = ${tags}\n}'} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500">Bicep template</Label>
        <Textarea rows={5} spellCheck={false} data-testid={`${testidPrefix}-bicep-code`} className="mt-2 border-slate-200 font-mono-tight text-xs" value={value.bicep_code} onChange={(e) => onChange({ ...value, bicep_code: e.target.value })} placeholder={"resource ${label} 'Microsoft.DocumentDB/databaseAccounts@2024-05-15' = {\n  name: '${name}'\n  location: ${location}\n  tags: ${tags}\n}"} />
      </div>
    </div>
  );
}

function DefaultsEditor({ open, onOpenChange, resource, onSave }) {
  const [rows, setRows] = useState([]);
  const [shortName, setShortName] = useState("");
  const [code, setCode] = useState({ terraform_code: "", bicep_code: "" });

  useEffect(() => {
    if (resource) {
      const d = resource.defaults || {};
      setRows(Object.entries(d).map(([k, v], i) => ({ id: `${k}-${i}`, k, v: String(v) })));
      setShortName(resource.short_name || "");
      setCode({ terraform_code: resource.terraform_code || "", bicep_code: resource.bicep_code || "" });
    }
  }, [resource]);

  const addRow = () => setRows([...rows, { id: `new-${Date.now()}`, k: "", v: "" }]);
  const setRow = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const rmRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    const defaults = {};
    rows.forEach((r) => {
      if (r.k.trim()) defaults[r.k.trim()] = r.v;
    });
    const patch = { defaults, short_name: shortName.trim() || null };
    if (resource.custom) {
      patch.terraform_code = code.terraform_code;
      patch.bicep_code = code.bicep_code;
    }
    await onSave(patch);
    onOpenChange(false);
  };

  if (!resource) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="defaults-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            Configure · <span className="font-mono-tight text-sm text-slate-500">{resource.key}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid="short-name-block">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// short code</p>
              <p className="text-xs text-slate-600">
                Segment injected into generated names (e.g. <span className="font-mono-tight text-slate-800">sa</span> in{" "}
                <span className="font-mono-tight text-slate-800">cor-myapp-<b>sa</b>-prod</span>).
              </p>
            </div>
            <Input
              data-testid="short-name-input"
              value={shortName}
              onChange={(e) => setShortName(e.target.value.replace(/[^a-z0-9]/gi, "").toLowerCase())}
              placeholder="sa"
              maxLength={8}
              className="h-9 w-24 border-slate-200 font-mono-tight text-sm"
            />
          </div>
        </div>

        <p className="mt-4 text-sm text-slate-600">
          Key/value defaults injected into every generated project that uses{" "}
          <span className="font-mono-tight text-xs">{resource.name}</span>.
        </p>
        <div className="mt-2 space-y-2" data-testid="defaults-rows">
          {rows.map((r, i) => (
            <div key={r.id} className="grid grid-cols-[1fr_1fr_auto] gap-2" data-testid={`default-row-${i}`}>
              <Input
                placeholder="key (e.g. sku_name)"
                value={r.k}
                onChange={(e) => setRow(i, "k", e.target.value)}
                className="h-9 border-slate-200 font-mono-tight text-sm"
                data-testid={`default-key-${i}`}
              />
              <Input
                placeholder="value"
                value={r.v}
                onChange={(e) => setRow(i, "v", e.target.value)}
                className="h-9 border-slate-200 font-mono-tight text-sm"
                data-testid={`default-value-${i}`}
              />
              <Button variant="ghost" size="icon" onClick={() => rmRow(i)} data-testid={`default-remove-${i}`}>
                <Trash2 className="h-4 w-4 text-slate-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={addRow} className="mt-2 h-9 border-dashed border-slate-300 text-slate-600" data-testid="default-add-row">
            <Plus className="mr-2 h-3.5 w-3.5" /> Add key
          </Button>
        </div>
        {resource.custom && <CodeTemplates value={code} onChange={setCode} testidPrefix="edit" />}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="defaults-cancel">Cancel</Button>
          <Button onClick={save} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="defaults-save">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddResourceDialog({ open, onOpenChange, cloud, onCreated }) {
  const [form, setForm] = useState({
    key: "",
    cloud,
    category: "Compute",
    name: "",
    description: "",
    short_name: "",
    terraform_type: "",
    bicep_type: "",
  });
  const [code, setCode] = useState({ terraform_code: "", bicep_code: "" });

  useEffect(() => {
    setForm((f) => ({ ...f, cloud }));
  }, [cloud, open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        short_name: form.short_name.trim() || null,
        terraform_type: form.terraform_type || null,
        bicep_type: form.bicep_type || null,
        defaults: {},
        terraform_code: code.terraform_code.trim() || null,
        bicep_code: code.bicep_code.trim() || null,
      };
      const { data } = await client.post("/catalog/resources", payload);
      toast.success(`${data.name} added to the catalog`);
      onCreated(data);
      onOpenChange(false);
      setForm({ ...form, key: "", name: "", description: "", short_name: "", terraform_type: "", bicep_type: "" });
      setCode({ terraform_code: "", bicep_code: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="add-resource-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add a resource to the catalog</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Key</Label>
              <Input required data-testid="new-res-key" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={form.key} onChange={set("key")} placeholder="azure_cosmos_db" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Cloud</Label>
              <Select value={form.cloud} onValueChange={(v) => setForm({ ...form, cloud: v })}>
                <SelectTrigger data-testid="new-res-cloud" className="mt-2 h-10 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="azure">Azure</SelectItem>
                  <SelectItem value="aws">AWS</SelectItem>
                  <SelectItem value="gcp">GCP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Display name</Label>
              <Input required data-testid="new-res-name" className="mt-2 h-10 border-slate-200" value={form.name} onChange={set("name")} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="new-res-category" className="mt-2 h-10 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Description</Label>
            <Textarea data-testid="new-res-description" rows={2} className="mt-2 border-slate-200" value={form.description} onChange={set("description")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Terraform type</Label>
              <Input data-testid="new-res-tf" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={form.terraform_type} onChange={set("terraform_type")} placeholder="azurerm_cosmosdb_account" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Bicep type</Label>
              <Input data-testid="new-res-bicep" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={form.bicep_type} onChange={set("bicep_type")} placeholder="Microsoft.DocumentDB/…" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Short code</Label>
            <Input
              data-testid="new-res-short"
              className="mt-2 h-10 w-28 border-slate-200 font-mono-tight text-sm"
              value={form.short_name}
              onChange={(e) => setForm({ ...form, short_name: e.target.value.replace(/[^a-z0-9]/gi, "").toLowerCase() })}
              placeholder="cosmos"
              maxLength={8}
            />
            <p className="mt-1 font-mono-tight text-[10px] text-slate-400">Injected as the &quot;resource&quot; segment in composed names.</p>
          </div>
          <CodeTemplates value={code} onChange={setCode} testidPrefix="new" />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="add-res-cancel">Cancel</Button>
            <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="add-res-submit">Add resource</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkImportDialog({ open, onOpenChange, onImported }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const parse = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) throw new Error("Paste at least one entry.");
    try {
      return JSON.parse(trimmed);
    } catch (_) {
      try {
        return yaml.load(trimmed);
      } catch (e) {
        throw new Error(`Could not parse as JSON or YAML: ${e.message}`);
      }
    }
  };

  const submit = async () => {
    setBusy(true);
    setResult(null);
    try {
      const parsed = parse(text);
      const entries = Array.isArray(parsed) ? parsed : (parsed?.entries || parsed?.resources || []);
      if (!Array.isArray(entries) || entries.length === 0) {
        throw new Error("Expected a list of resource entries.");
      }
      const { data } = await client.post("/catalog/resources/bulk", { entries });
      setResult(data);
      const msg = `${data.created.length} created · ${data.skipped.length} skipped · ${data.errors.length} error`;
      if (data.created.length) toast.success(msg);
      else toast.warning(msg);
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const sample = `- key: azure_cosmos_db
  cloud: azure
  category: Database
  name: Cosmos DB
  description: NoSQL DB with global distribution
  terraform_type: azurerm_cosmosdb_account
  short_name: csm
  defaults:
    consistency_level: Session

- key: aws_sqs_queue
  cloud: aws
  category: Messaging
  name: SQS Queue
  description: Managed queue
  terraform_type: aws_sqs_queue
  short_name: sqs
`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Bulk import resources</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Paste a <span className="font-mono-tight text-slate-800">YAML</span> or{" "}
          <span className="font-mono-tight text-slate-800">JSON</span> list of resource entries. Each entry supports{" "}
          <span className="font-mono-tight text-xs">key · cloud · category · name · description · terraform_type · bicep_type · short_name · defaults</span>.
        </p>
        <div className="mt-3">
          <div className="mb-2 flex items-center justify-between">
            <label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// paste below</label>
            <button
              type="button"
              onClick={() => setText(sample)}
              className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-900"
              data-testid="bulk-sample"
            >
              load sample
            </button>
          </div>
          <Textarea
            data-testid="bulk-textarea"
            rows={12}
            className="border-slate-200 font-mono-tight text-xs"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={sample}
          />
        </div>
        {result && (
          <div className="mt-3 space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-4 text-sm" data-testid="bulk-result">
            <div className="grid grid-cols-3 gap-3 text-center font-mono-tight text-xs">
              <div><div className="text-lg text-emerald-600">{result.created.length}</div><div className="uppercase tracking-widest text-slate-500">created</div></div>
              <div><div className="text-lg text-amber-600">{result.skipped.length}</div><div className="uppercase tracking-widest text-slate-500">skipped</div></div>
              <div><div className="text-lg text-red-600">{result.errors.length}</div><div className="uppercase tracking-widest text-slate-500">errors</div></div>
            </div>
            {result.skipped.length > 0 && (
              <div className="border-t border-slate-200 pt-2 text-xs">
                <p className="font-mono-tight uppercase tracking-widest text-slate-500">skipped</p>
                <ul className="mt-1 space-y-0.5">
                  {result.skipped.map((s) => <li key={`${s.key}-${s.reason}`} className="font-mono-tight text-slate-600">{s.key} — {s.reason}</li>)}
                </ul>
              </div>
            )}
            {result.errors.length > 0 && (
              <div className="border-t border-slate-200 pt-2 text-xs">
                <p className="font-mono-tight uppercase tracking-widest text-red-500">errors</p>
                <ul className="mt-1 space-y-0.5">
                  {result.errors.map((e, i) => <li key={`${e.key || i}-${e.reason}`} className="font-mono-tight text-red-600">{e.key || "?"} — {e.reason}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="bulk-close">Close</Button>
          <Button onClick={submit} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="bulk-submit">
            <Upload className="mr-2 h-4 w-4" /> {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "verified", label: "Verified" },
  { key: "unverified", label: "Not verified" },
  { key: "custom", label: "Custom" },
  { key: "builtin", label: "Built-in" },
];
const CLOUDS = [
  { key: "azure", label: "Azure", hint: "azurerm · bicep" },
  { key: "aws", label: "AWS", hint: "aws provider" },
  { key: "gcp", label: "GCP", hint: "google provider" },
];

function matchesFilters(r, { query, categories, status }) {
  const q = query.trim().toLowerCase();
  if (q && !`${r.name} ${r.key} ${r.terraform_type || ""} ${r.bicep_type || ""} ${r.description || ""}`.toLowerCase().includes(q)) return false;
  if (categories.size && !categories.has(r.category)) return false;
  const verified = !!(r.verified?.checked && r.verified?.any);
  if (status === "verified" && !verified) return false;
  if (status === "unverified" && verified) return false;
  if (status === "custom" && !r.custom) return false;
  if (status === "builtin" && r.custom) return false;
  return true;
}

function ProviderPane({ items, active, onSelect }) {
  return (
    <aside className="sticky top-0 flex h-screen w-[300px] min-w-[300px] max-w-[300px] shrink-0 flex-col self-start overflow-hidden border-r border-slate-200 bg-white" data-testid="catalog-provider-pane">
      <p className="flex h-16 items-center border-b border-slate-200 px-4 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// cloud providers</p>
      <ul className="flex-1 overflow-y-auto p-2">
        {CLOUDS.map((c) => {
          const list = items.filter((i) => i.cloud === c.key && !i.disabled);
          const verified = list.filter((i) => i.verified?.checked && i.verified?.any).length;
          const isActive = active === c.key;
          return (
            <li key={c.key}>
              <button
                type="button"
                onClick={() => onSelect(c.key)}
                data-testid={`provider-${c.key}`}
                aria-pressed={isActive}
                className={cn("flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors", isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}
              >
                <CloudIcon cloud={c.key} className="h-5 w-5 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-sm font-semibold">{c.label}</span>
                  <span className={cn("block truncate font-mono-tight text-[10px] uppercase tracking-widest", isActive ? "text-slate-300" : "text-slate-400")}>{c.hint}</span>
                </span>
                <span className="text-right font-mono-tight text-[11px]" data-testid={`provider-count-${c.key}`}>
                  {list.length}
                  <span className={cn("block text-[10px]", isActive ? "text-sky-300" : "text-sky-600")}>{verified} verified</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function Filters({ query, setQuery, categories, toggleCategory, status, setStatus, available }) {
  return (
    <div className="mb-6 space-y-3" data-testid="catalog-filters">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[260px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or type…" data-testid="catalog-search" className="h-10 border-slate-200 pl-9" />
        </div>
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="status-filter">
          {STATUS_FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setStatus(f.key)} data-testid={`status-${f.key}`} aria-pressed={status === f.key}
              className={cn("rounded px-3 py-1.5 text-xs transition-colors", status === f.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="category-filter">
        {available.map((c) => (
          <button key={c} type="button" onClick={() => toggleCategory(c)} data-testid={`category-${c}`} aria-pressed={categories.has(c)}
            className={cn("rounded-full border px-3 py-1 font-mono-tight text-[11px] uppercase tracking-widest transition-colors", categories.has(c) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400")}>
            {c}
          </button>
        ))}
        {categories.size > 0 && (
          <button type="button" onClick={() => toggleCategory(null)} data-testid="category-clear" className="px-2 font-mono-tight text-[11px] text-slate-500 underline-offset-4 hover:underline">clear</button>
        )}
      </div>
    </div>
  );
}

export default function Catalog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [activeCloud, setActiveCloud] = useState("azure");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [registryOpen, setRegistryOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState(() => new Set());
  const [status, setStatus] = useState("all");
  const [showDeleted, setShowDeleted] = useState(false);

  const load = useCallback(() => {
    client.get(`/catalog/resources${isAdmin ? "?all=true" : ""}`).then((r) => setItems(r.data));
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const saveDefaults = async (patch) => {
    try {
      await client.patch(`/catalog/resources/${editing.key}`, patch);
      toast.success("Resource updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const [removing, setRemoving] = useState(null);
  const remove = async (r) => {
    try {
      await client.delete(`/catalog/resources/${r.key}`);
      toast.success("Resource deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const restore = async (r) => {
    try {
      await client.post(`/catalog/resources/${r.key}/restore`);
      toast.success("Restored");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const toggleCategory = (c) => setCategories((prev) => {
    if (c === null) return new Set();
    const n = new Set(prev);
    n.has(c) ? n.delete(c) : n.add(c);
    return n;
  });

  const cloudItems = items.filter((i) => i.cloud === activeCloud);
  const live = cloudItems.filter((i) => !i.disabled);
  const deleted = cloudItems.filter((i) => i.disabled);
  const available = [...new Set(live.map((i) => i.category))];
  const visible = live.filter((r) => matchesFilters(r, { query, categories, status }));
  const grouped = visible.reduce((acc, r) => { (acc[r.category] = acc[r.category] || []).push(r); return acc; }, {});
  const cardProps = { isAdmin, onConfigure: setEditing, onDelete: setRemoving, onRestore: restore };

  return (
    <div className="grid grid-cols-[300px_minmax(0,1fr)]" data-testid="catalog-layout">
      <ConfirmDeleteDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} name={removing?.name || ""} title="Delete catalog resource" verb="Delete resource"
        description="It will no longer be selectable in the generator. Admins can restore it later from the deleted filter." onConfirm={() => remove(removing)} testid="confirm-resource" />
      <ProviderPane items={items} active={activeCloud} onSelect={(c) => { setActiveCloud(c); setCategories(new Set()); }} />

      <div className="min-w-0 p-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// catalog</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="page-title">Approved resources</h1>
          <p className="mt-1 text-sm text-slate-600">
            Every resource in the catalog is approved for generation. <BadgeCheck className="inline h-4 w-4 text-sky-600" /> marks resources
            present in an official provider release (Terraform Registry / Azure Bicep types).
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setRegistryOpen(true)} className="h-10 rounded-md border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100" data-testid="registry-import">
              <Globe className="mr-2 h-4 w-4" /> Import from registry
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)} className="h-10 rounded-md border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="bulk-import">
              <Upload className="mr-2 h-4 w-4" /> Bulk import
            </Button>
            <Button onClick={() => setAdding(true)} className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800" data-testid="add-resource">
              <Plus className="mr-2 h-4 w-4" /> Add resource
            </Button>
          </div>
        )}
      </div>

        <div className="min-w-0" data-testid={`catalog-${activeCloud}`}>
          <Filters query={query} setQuery={setQuery} categories={categories} toggleCategory={toggleCategory} status={status} setStatus={setStatus} available={available} />

          <p className="mb-4 font-mono-tight text-[11px] text-slate-500" data-testid="catalog-result-count">{visible.length} of {live.length} resources</p>
          {visible.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="catalog-empty">No resource matches the current filters.</div>
          )}
          <div className="space-y-10">
            {Object.entries(grouped).map(([cat, rows]) => (
              <div key={cat}>
                <p className="mb-4 font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">// {cat.toLowerCase()} · {rows.length}</p>
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                  {rows.map((r) => <ResourceCard key={r.key} r={r} {...cardProps} />)}
                </div>
              </div>
            ))}
          </div>

          {isAdmin && deleted.length > 0 && (
            <div className="mt-12 border-t border-slate-200 pt-6" data-testid="deleted-section">
              <button type="button" onClick={() => setShowDeleted((v) => !v)} data-testid="toggle-deleted" className="flex items-center gap-2 font-mono-tight text-[11px] uppercase tracking-widest text-slate-500 hover:text-slate-900">
                {showDeleted ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                Deleted resources · {deleted.length}
              </button>
              {showDeleted && (
                <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
                  {deleted.map((r) => <ResourceCard key={r.key} r={r} {...cardProps} />)}
                </div>
              )}
            </div>
          )}
        </div>

      <DefaultsEditor open={!!editing} onOpenChange={(v) => !v && setEditing(null)} resource={editing} onSave={saveDefaults} />
      <AddResourceDialog open={adding} onOpenChange={setAdding} cloud={activeCloud} onCreated={() => load()} />
      <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} onImported={() => load()} />
      <RegistryImportDialog open={registryOpen} onOpenChange={setRegistryOpen} cloud={activeCloud} onImported={() => load()} />
      </div>
    </div>
  );
}
